import { useMemo } from 'react';

import { useNavigate, useLocation } from 'react-router';

import useI18n from '@/hooks/useI18n';
import type { MethodsOptions } from '@/hooks/useRouter/methodsOptions';
import resolveNavigateOptions from '@/hooks/useRouter/resolveNavigateOptions';
import normalizePath from '@/hooks/useRouter/normalizePath';
import { queryClient } from '@/queryClient';
import Logger from '@/lib/logger';

const approach = 1;
export function getCurrentURI(normalized = false) {
  const { href, origin, pathname } = window.location;

  if (approach === 1) {
    return href.replace(origin, '');
  }

  return normalized ? normalizePath(pathname) : pathname;
}

export default function useRouter() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { locale } = useI18n();

  return useMemo(
    () => ({
      push(path: string, options: MethodsOptions = {}) {
        Logger.trace('useRouter:push', { path, options });
        navigate(
          `/${locale}/${normalizePath(path)}`,
          resolveNavigateOptions(options)
        );
      },
      replace(path: string, options: MethodsOptions = {}) {
        Logger.trace('useRouter:replace', { path, options });
        navigate(
          `/${locale}/${normalizePath(path)}`,
          resolveNavigateOptions(options, 'replace')
        );
      },
      back() {
        Logger.trace('useRouter:back');
        navigate(-1);
      },
      redirectToLogin(useCurrentPath: boolean = true, redirectUri?: string) {
        const finalRedirectUri = useCurrentPath ? getCurrentURI() : redirectUri;
        queryClient.removeQueries();

        Logger.trace('useRouter:redirectToLogin', { finalRedirectUri });

        if (finalRedirectUri) {
          navigate(
            `/${locale}/login?redirectUri=${encodeURIComponent(finalRedirectUri.replace(`/${locale}`, ''))}`
          );
        } else {
          navigate(`/${locale}/login`);
        }
      },
      removeURLSearchParam(param: string, options: MethodsOptions = {}) {
        const nextQuery = new URLSearchParams(search);
        nextQuery.delete(param);
        navigate(
          {
            pathname,
            search: nextQuery.toString() ? `?${nextQuery.toString()}` : '',
          },
          resolveNavigateOptions(options, 'replace')
        );
      },
    }),
    [locale, navigate, pathname, search]
  );
}
